import { useState } from 'react';
import { useTranslatedContent } from '../useTranslatedContent';
import { validateTrainingRequest } from '../../services/email/utils/emailValidation';
import { trackConversion } from '../../utils/tracking/adsTracking';
import type { TrainingFormData } from '../../types/training';

const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;

const initialFormData: TrainingFormData = {
  name: '',
  email: '',
  company: '',
  participants: '',
  trainingType: '',
  preferredDate: '',
  message: ''
};

export const useTrainingForm = () => {
  const { t, currentLanguage } = useTranslatedContent('training');
  const [formData, setFormData] = useState<TrainingFormData>(initialFormData);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateTrainingRequest(formData)) {
      setError(t('request.validation'));
      return;
    }

    setLoading(true);
    setError(null);
    setSuccess(false);

    try {
      const submissionUrl = `${supabaseUrl}/functions/v1/training-request-submission`;
      const response = await fetch(submissionUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          name: formData.name,
          email: formData.email,
          company: formData.company,
          participants: parseInt(formData.participants) || 1,
          trainingType: formData.trainingType,
          preferredDate: formData.preferredDate || undefined,
          message: formData.message || undefined,
          language: currentLanguage
        })
      });

      if (!response.ok) {
        const result = await response.json();
        throw new Error(result.error || 'Failed to submit form');
      }

      setSuccess(true);
      setFormData(initialFormData);
      trackConversion('trainingRequest');
    } catch (err) {
      console.error('Training request error:', err);
      setError(t('request.error'));
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    if (error) setError(null);
  };

  return {
    formData,
    loading,
    error,
    success,
    handleSubmit,
    handleChange
  };
};